import React, { useEffect, useState } from "react";
import { clearSession } from "../../shared/_helpers/session";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getProfile, blockUnblockUser, incrementTotalMeetings } from "../../shared/_services/common.services";
import { formatMeetingsAvailable } from "../../shared/_helpers/meetingEntitlement";
import {
  Button, Dialog, DialogContent, DialogTitle, DialogDescription, DialogActions, DialogClose,
} from "../ui";
import StarsIcon from "../../styles/StarsIcon";
import { useSnackbar } from "notistack";
import moment from "moment";
import { useAuth } from "../../context/AuthContext.jsx";
import { getSelectedPlanName, getSelectedPlanStatusLabel } from "../../shared/_helpers/selectedPlan";

function isPrivilegedRole(role) {
  return role === 'super-admin' || role === 'admin';
}

function isDocumentsNavActive(pathname) {
  return pathname.includes('documents') || pathname.includes('dashboard');
}

function isMeetingsNavActive(pathname) {
  return pathname.includes('meetings') || pathname.includes('dashboard');
}

function formatMemberSince(createdAt) {
  return createdAt ? moment(createdAt).format('dddd D MMMM YYYY') : '-';
}

function buildBlockConfirmMessage(clientDetail) {
  const action = clientDetail?.status ? 'block' : 'unblock';
  const firstName = clientDetail?.firstName ?? '';
  return `You are attempting to ${action} ${firstName}.`;
}

function resolveBlockButtonLabel(blockAction) {
  return blockAction === 'block' ? 'Block' : 'Unblock';
}

async function loadClientProfile(userId, setClientDetail, enqueueSnackbar, navigate) {
  const data = await getProfile(userId);
  if (data.status === 200) {
    setClientDetail(data?.data?.data?.user);
    return;
  }
  if (data?.response?.status !== 401) {
    return;
  }
  clearSession();
  enqueueSnackbar("Your session has been expired!", {
    variant: 'error',
    anchorOrigin: { horizontal: 'right', vertical: 'top' },
  });
  navigate('/login');
}

function navigateBackFromClientArea(navigate, currentPath, clientId) {
  if (currentPath.includes('/documents') || currentPath.includes('/meetings')) {
    navigate(`/dashboard/${clientId}?client=true`);
    return;
  }
  if (currentPath.includes('/dashboard')) {
    navigate('/users');
  }
}

function ClientAvatar({ clientDetail }) {
  if (!clientDetail.hasOwnProperty('firstName')) {
    return null;
  }
  return (
    <div className="tw-mr-3 tw-grid tw-h-12 tw-w-12 tw-flex-none tw-place-items-center tw-overflow-hidden tw-rounded-pill tw-bg-surface-selected tw-text-sm tw-font-bold tw-text-text">
      {clientDetail.profile_image ? (
        <img className="tw-h-12 tw-w-12 tw-rounded-pill tw-object-cover" src={clientDetail.profile_image} alt="" />
      ) : (
        <span>
          {clientDetail.firstName?.toUpperCase().trim().charAt(0)}
          {clientDetail.lastName?.toUpperCase().trim().charAt(0)}
        </span>
      )}
    </div>
  );
}

function DashboardUserHeader() {
  const [clientDetail, setClientDetail] = useState({});
  const [updatingMeetings, setUpdatingMeetings] = useState(false);
  const [subscriptionDetailsPopup, setSubscriptionDetailsPopup] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const pathname = location.pathname;
  const showDocuments = pathname.includes('documents');
  const showMeetings = pathname.includes('meetings');
  // SEC-066: the Block, Subscription Details and Increment-Meeting actions
  // are gated by the server-verified role — this header only ever renders
  // inside a ProtectedRoutes subtree, so verification has already settled by
  // the time it mounts.
  const { role } = useAuth();
  const [confirmBlockPopupShow, setConfirmBlockPopupShow] = useState(false);
  const [blockAction, setBlockAction] = useState('');
  const userId = params?.clientId;
  const canIncrement = isPrivilegedRole(role?.toLowerCase());
  const getClientDetails = async () => {
    await loadClientProfile(userId, setClientDetail, enqueueSnackbar, navigate);
  };

  useEffect(() => {
    getClientDetails();
  }, [userId])

  const handleSubscriptionClick = () => {
    setSubscriptionDetailsPopup(true);
  };

  const subscriptionDetailsPopupClose = () => {
    setSubscriptionDetailsPopup(false);
  };

  const handleGoBack = () => {
    navigateBackFromClientArea(navigate, location.pathname, clientDetail?._id);
  };

  const openConfirmBlockPopup = () => {
    setBlockAction(clientDetail?.status ? 'block' : 'unblock');
    setConfirmBlockPopupShow(true);
  };

  const confirmBlockPopupClose = () => {
    setConfirmBlockPopupShow(false);
  };

  const handleBlock = async () => {
    setConfirmBlockPopupShow(false);
    try {
      const newStatus = !clientDetail?.status;
      await blockUnblockUser({
        userId: clientDetail?._id,
        status: newStatus,
      });
      const action = newStatus ? "unblocked" : "blocked";
      enqueueSnackbar(`Client ${action} successfully`, { variant: "success" });
      getClientDetails();
    } catch (error) {
      const action = clientDetail?.status ? "block" : "unblock";
      enqueueSnackbar(`Failed to ${action} client`, { variant: "error" });
    }
  };

  const handleIncrementMeetings = async () => {
    if (!clientDetail?._id) return;
    setUpdatingMeetings(true);
    
    try {
      const res = await incrementTotalMeetings(clientDetail._id);
      if (res.status == 200) {
        // Backend ARCH-036 changed what this route answers with. It used to return
        // `total_meetings`, the pooled counter the grant had just bumped; that
        // counter is gone and the grant now moves `purchased_meetings` — the bucket
        // that never expires and survives a plan change, which is what makes this
        // button a comp rather than a loan against the annual allowance. The
        // response carries that plus the recomputed `meetings_available`, so the
        // header below can show the new balance without re-reading the profile.
        setClientDetail((prev) => ({
          ...prev,
          purchased_meetings: res.purchased_meetings,
          meetings_available: res.meetings_available
        }));

        setTimeout(() => {
          setUpdatingMeetings(false);
        }, 300);

        enqueueSnackbar("Meeting credit granted", { variant: "success" });
      }
    } catch (err) {
      enqueueSnackbar("Failed to increment total meetings", { variant: "error" });
    }
  };

  const isPrivilegedAdvisor = isPrivilegedRole(role);
  const meetingsListQuery = isPrivilegedAdvisor ? '?isadmin=true' : '';
  const documentsNavActive = isDocumentsNavActive(pathname);
  const meetingsNavActive = isMeetingsNavActive(pathname);
  // #91: plan from the price nickname, status from the subscription's own
  // status, not from the billing interval and the price's `active` flag.
  const subscriptionPlanLabel = getSelectedPlanName(clientDetail?.selected_plan);
  const subscriptionStatusLabel = getSelectedPlanStatusLabel(clientDetail?.selected_plan);
  const memberSinceFormatted = formatMemberSince(clientDetail?.createdAt);
  const blockConfirmMessage = buildBlockConfirmMessage(clientDetail);

  const goToPastMeetings = () => {
    navigate(`/meetings/${clientDetail?._id}${meetingsListQuery}`);
  };

  return (
    <>
      <div className="tw-mb-8">
        <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-4">
          <div className="tw-flex tw-min-w-0 tw-items-start tw-gap-3">
            <Button variant="icon" iconOnly onClick={handleGoBack} aria-label="Back">
              <StarsIcon name="chevronLeft" className="tw-h-4 tw-w-4" />
            </Button>

            {!showDocuments && !showMeetings &&
              <div className="tw-flex tw-items-center">
                <ClientAvatar clientDetail={clientDetail} />
                <div>
                  <h6 className="tw-m-0 tw-text-xl tw-font-bold tw-text-text">
                    {clientDetail?.firstName}{" "}
                    {clientDetail?.lastName}
                  </h6>
                  <div className="tw-mt-1 tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1">
                    <div id="email" className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-text-muted">
                      <StarsIcon name="mail" className="tw-h-4 tw-w-4 tw-shrink-0" />
                      {clientDetail?.email}
                    </div>
                    <div id="phone" className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-text-muted">
                      <StarsIcon name="message" className="tw-h-4 tw-w-4 tw-shrink-0" />
                      {clientDetail?.mobile}
                    </div>
                  </div>
                </div>

              </div>}

            {pathname.includes('documents') && <h2 className="tw-m-0 tw-mt-2 tw-text-2xl tw-font-bold tw-text-text">Documents</h2>}
            {pathname.includes('meetings') && <h2 className="tw-m-0 tw-mt-2 tw-text-2xl tw-font-bold tw-text-text">Meetings</h2>}

          </div>

          <div className="tw-ml-auto tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3">
             {/* Meetings the client can book right now. Was `consumed/total`,
                 which rendered a flat `0/0` for every client once backend ARCH-036
                 removed both counters — indistinguishable from a genuinely
                 exhausted account, on the one screen an admin uses to decide
                 whether to grant a credit. */}
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-text-muted">
              <span>Meetings Available:</span>
              <span className="tw-font-bold tw-text-text tw-tabular-nums">
                {formatMeetingsAvailable(clientDetail)}
              </span>

              {canIncrement && (
                <Button
                  size="sm"
                  onClick={handleIncrementMeetings}
                  disabled={updatingMeetings}
                >
                  {updatingMeetings ? "Updating..." : "Add Meeting"}
                </Button>
              )}
            </div>

            <Button size="sm" variant={documentsNavActive ? 'primary' : 'quiet'} onClick={() => navigate(`/documents/${clientDetail?._id}`)}><span>Documents</span></Button>

            {/* <button type="button" className={`as-btn-primary btn-sharp mt-1 ${location.pathname.includes('meetings') || location.pathname.includes('dashboard') ? 'active' : ''}`} onClick={() => navigate(`/meetings/${clientDetail?._id}`)}><span>Past Meetings</span></button> */}
            <Button
              size="sm"
              variant={meetingsNavActive ? 'primary' : 'quiet'}
              onClick={goToPastMeetings}
            >
              <span>Past Meetings</span>
            </Button>

            {isPrivilegedAdvisor && (
              <Button size="sm" variant={pathname.includes('dashboard') ? 'primary' : 'quiet'} onClick={handleSubscriptionClick}><span>Subscription Details</span></Button>
            )}

            {isPrivilegedAdvisor && (
              <Button
                id="block-user"
                variant="icon"
                iconOnly
                onClick={openConfirmBlockPopup}
                title={clientDetail?.status ? 'Block' : 'Unblock'}
                aria-label={clientDetail?.status ? 'Block' : 'Unblock'}
                data-blocked={clientDetail?.status ? undefined : "true"}
              >
                <StarsIcon name="ban" className={clientDetail?.status ? "tw-h-5 tw-w-5 tw-shrink-0" : "tw-h-5 tw-w-5 tw-shrink-0 tw-text-danger"} />
              </Button>
            )}
          </div>
        </div>

      </div>

      {/* subscription details popup */}
      <Dialog open={subscriptionDetailsPopup} onOpenChange={(o) => !o && subscriptionDetailsPopupClose()}>
        <DialogContent
          aria-describedby={undefined}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
            <DialogTitle>Subscription Details</DialogTitle>
            <DialogClose asChild>
              <Button variant="icon" size="sm" iconOnly aria-label="Close">
                <StarsIcon name="x" className="tw-h-4 tw-w-4" />
              </Button>
            </DialogClose>
          </div>
          <dl className="tw-m-0 tw-mt-4 tw-grid tw-grid-cols-3 tw-gap-x-4 tw-gap-y-3 tw-text-sm">
            <dt className="tw-flex tw-items-center tw-gap-2 tw-text-text-muted">
              <StarsIcon name="card" className="tw-h-4 tw-w-4 tw-shrink-0" />
              Subscription Type
            </dt>
            <dd className="tw-col-span-2 tw-m-0 tw-font-semibold">{subscriptionPlanLabel}</dd>
            <dt className="tw-flex tw-items-center tw-gap-2 tw-text-text-muted">
              <StarsIcon name="check" className="tw-h-4 tw-w-4 tw-shrink-0" />
              Status
            </dt>
            <dd className="tw-col-span-2 tw-m-0 tw-font-semibold">{subscriptionStatusLabel}</dd>
            <dt className="tw-flex tw-items-center tw-gap-2 tw-text-text-muted">
              <StarsIcon name="calendar" className="tw-h-4 tw-w-4 tw-shrink-0" />
              Member Since
            </dt>
            <dd className="tw-col-span-2 tw-m-0 tw-font-semibold">{memberSinceFormatted}</dd>
          </dl>
        </DialogContent>
      </Dialog>

      {/* block popup */}
      <Dialog open={confirmBlockPopupShow} onOpenChange={(o) => !o && confirmBlockPopupClose()}>
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
            <DialogTitle>{blockConfirmMessage}</DialogTitle>
            <DialogClose asChild>
              <Button variant="icon" size="sm" iconOnly aria-label="Close">
                <StarsIcon name="x" className="tw-h-4 tw-w-4" />
              </Button>
            </DialogClose>
          </div>
          <DialogDescription>Are you sure?</DialogDescription>
          <DialogActions>
            <Button variant="danger" onClick={handleBlock}>
              {resolveBlockButtonLabel(blockAction)}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>

  );
}

export default DashboardUserHeader;
